/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { EcsperError } from '@ecsper/types';

import { HEADER_REGION } from './constants.js';
import { EcsperApiError, isEcsperErrorResponse } from './errors.js';

/** Synthetic error used when the server returns a non-JSON or unparseable error body. */
function syntheticError(status: number, statusText: string, body: string): EcsperError {
  return {
    code: 'ECSPER_API_UNKNOWN_ERROR',
    title: `HTTP ${String(status)} ${statusText}`,
    detail:
      body.length > 0
        ? `Server returned non-standard error body: ${body.slice(0, 500)}`
        : `Server returned ${String(status)} with no body.`,
  };
}

/**
 * Parse a successful 2xx JSON response into the typed shape `T`.
 *
 * Treats an empty body as `undefined`; callers requesting a void response can
 * use `T = void` and discard the result.
 */
export async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

/**
 * Parse a successful 2xx response as a binary `Blob`. Used for DOCX downloads
 * via `GET /workspace/dl/{token}` (ECS-DD-MS365-001 §B4.2).
 */
export async function parseBlobResponse(response: Response): Promise<Blob> {
  return response.blob();
}

/**
 * Convert a non-2xx `Response` into an {@link EcsperApiError}.
 *
 * The standard envelope (CTR-MS365-CONTRACTS §"Error Contract" /
 * ECS-DD-MS365-001 §B4) is parsed where present; otherwise a single synthetic
 * `EcsperError` is constructed from the status line and raw body excerpt.
 */
export async function buildApiError(
  response: Response,
  requestId: string,
): Promise<EcsperApiError> {
  const body = await response.text();
  let parsed: unknown;
  try {
    parsed = body.length > 0 ? JSON.parse(body) : undefined;
  } catch {
    parsed = undefined;
  }
  const errors = isEcsperErrorResponse(parsed)
    ? parsed.errors
    : [syntheticError(response.status, response.statusText, body)];
  return new EcsperApiError({
    status: response.status,
    statusText: response.statusText,
    errors,
    requestId,
    responseBody: body.length > 0 ? body : undefined,
  });
}

/**
 * Read the `X-Ecsper-Region` response header (residency region routing per
 * CTR-PL-MS-OFFICESSO-001 §5.1). Returns `null` when absent.
 */
export function readRegion(response: Response): string | null {
  return response.headers.get(HEADER_REGION);
}
